import { Component, inject, signal, OnDestroy, OnInit, computed } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Subscription as RxSubscription } from "rxjs";
import { AuthService } from "../../services/AuthService";
import { MatSortModule, Sort } from "@angular/material/sort";
import { DialogAuthComponent } from "../../components/dialogs/dialog-auth/dialog-auth";
import { Apollo, ApolloBase } from "apollo-angular";
import { LIST_QUERIES } from "../../graphql/replication/subscription-list";
import { ProductType } from "../../helpers/enums/productType";
import { DialogIPPrefixesComponent } from "../../components/dialogs/dialog-ip-prefixes/dialog-ip-prefixes";
import { openSelfServiceDialog } from "../../components/dialogs/dialog-selfservice/dialog-selfservice.helpers";
import { DatePipe } from "@angular/common";
import { NavigationComponent } from "../../components/common/navigation/navigation";
import { TranslateModule } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { FooterComponent } from "../../components/common/footer/footer";
import { RequestAddPrefix } from "./components/reqeust-add-prefix/request-add-prefix";

@Component({
  selector: "page-ip-prefixes",
  templateUrl: "ip-prefixes.html",
  styleUrls: ["ip-prefixes.scss"],
  imports: [
    AngularSvgIconModule,
    DatePipe,
    FooterComponent,
    MatSortModule,
    NavigationComponent,
    TranslateModule,
    RequestAddPrefix,
  ],
})
export class IPPrefixesPage implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  private auth = inject(AuthService);
  private apolloProvider = inject(Apollo);

  private prefixes = signal<any>([]);

  protected subscriptions = computed(() => this.prefixes().filter((prefix) => prefix.ipPrefix.state !== 3));
  protected plannedSubscriptions = computed(() => this.prefixes().filter((prefix) => prefix.ipPrefix.state === 3));

  protected subscriptionsLength = computed(() => this.subscriptions().length);
  protected plannedSubscriptionsLength = computed(() => this.plannedSubscriptions().length);

  protected sortedData = [];

  private replicatedData: ApolloBase;
  private rxSubscriptions: RxSubscription[] = [];

  constructor() {
    this.replicatedData = this.apolloProvider.use("replicatedData");
  }

  ngOnInit() {
    this.rxSubscriptions.push(
      this.auth.userLoaded.subscribe((d) => {
        this.fetch();
      }),
    );
  }

  ngOnDestroy(): void {
    this.rxSubscriptions.forEach((s) => s.unsubscribe());
  }

  fetch() {
    this.replicatedData
      .query<any>({
        query: LIST_QUERIES[ProductType.IPPrefixes],
        variables: { customer: localStorage.getItem("viewingCustomerGUID") },
      })
      .subscribe(({ data }) => {
        this.prefixes.set(data.ipPrefixList.page);
        this.sortedData = [...this.subscriptions()];
      });
  }

  ipPrefixVersion(subscription) {
    return subscription?.ipPrefix?.afi ? "IPv" + subscription?.ipPrefix?.afi : "";
  }

  userCanAddPrefixes() {
    return this.auth?.hasRole(["Infraverantwoordelijke"]) && this.auth?.isSelfServiceEnabled;
  }

  userCanEditPrefixes(sub) {
    return (
      this.userCanAddPrefixes()
      && this.auth?.isCurrentOrganisation(sub.customerId)
      && !sub?.ipPrefix?.prefix?.startsWith("145.98")
    );
  }

  sortData(sort: Sort) {
    const data = this.sortedData;

    if (!sort.active || sort.direction === "") {
      this.sortedData = data;
      return;
    }

    this.sortedData = data.sort((subA, subB) => {
      const isAsc = sort.direction === "asc";
      switch (sort.active) {
        case "description":
          return this.compare(subA.description, subB.description, isAsc);
        case "prefix":
          let prefixA = subA?.ipPrefix?.prefix;
          let prefixB = subB?.ipPrefix?.prefix;
          if (prefixA === undefined) {
            prefixA = "";
          }
          if (prefixB === undefined) {
            prefixB = "";
          }
          // convert : and / to .
          prefixA = prefixA.replace(":", ".").replace("/", ".");
          prefixB = prefixB.replace(":", ".").replace("/", ".");

          prefixA = prefixA
            .split(".")
            .map((num) => num.padStart(3, "0"))
            .join("");
          prefixB = prefixB
            .split(".")
            .map((num) => num.padStart(3, "0"))
            .join("");

          // remove non numeric data
          prefixA = prefixA.replace(/\D/g, "");
          prefixB = prefixB.replace(/\D/g, "");

          prefixA = Number(prefixA);
          prefixB = Number(prefixB);

          return this.compare(prefixA, prefixB, isAsc);
        case "startdate":
          return this.compare(subA?.startDate, subB?.startDate, isAsc);
        case "version":
          const versionA = subA?.ipPrefix?.afi;
          const versionB = subB?.ipPrefix?.afi;

          return this.compare(versionA, versionB, isAsc);
        case "in_use":
          const inuseA = subA?.ipSubscriptions?.length;
          const inuseB = subB?.ipSubscriptions?.length;

          return this.compare(inuseA, inuseB, isAsc);
        default:
          return 0;
      }
    });
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  openDialog(subscriptions, linkedSubscriptions) {
    const dialogRef = this.dialog.open(DialogIPPrefixesComponent, {
      data: {
        subscriptions,
        linkedSubscriptions,
        editModus: false,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.forceScrollTop === true) {
        window.scrollTo(0, 0);
      }
    });
  }

  openAddDialog(subscription) {
    const dialogRef = this.dialog.open(DialogIPPrefixesComponent, {
      data: {
        subscriptions: subscription,
        linkedSubscriptions: subscription.availableIpSubscriptions,
        editModus: true,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.forceScrollTop === true) {
        window.scrollTo(0, 0);
      }
    });
  }

  openSelfserviceDialog(subscription) {
    const roleCheckResult = this.auth.checkRoleAccess("IP_PREFIX", "edit");
    if (!roleCheckResult.ok) {
      this.auth.roleEvent.emit(roleCheckResult);
      return;
    }

    if (!this.auth.selfServiceStarted()) {
      this.dialog.open(DialogAuthComponent, {
        data: {
          selfserviceState: { subscription },
          initialAction: "autoMitigation",
        },
      });
    } else {
      const data = { type: "autoMitigation", subscription };
      const dialogRef = openSelfServiceDialog(this.dialog, data);
      dialogRef.afterClosed().subscribe((event: string) => {
        if (event === "refresh") {
          this.fetch();
        }
      });
    }
  }
}
